import { Injectable } from "@angular/core";
import { FormioComponentSelectors } from "./formio-components-selectors";

import { FormioFormValuesParserService } from "./formio-form-values.service";

@Injectable({
    providedIn: "root"
})
export class FormioHtmlParserService {
    private parser: DOMParser = new DOMParser();
    private html: Document = null;

    constructor(private formValuesParser: FormioFormValuesParserService) {
    }

    public parseFromElement(formElement: HTMLElement, formData?: any): Document {
        // Get all input, textarea and select HTML elements.
        const inputs: NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> =
            formElement.querySelectorAll("input, textarea, select");

        // For each of them, update their value attribute with their actual value.
        inputs.forEach((input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): void => {
            if (!!input.value) input.setAttribute("value", input.value);
        });

        const html: string = formElement.outerHTML;

        return this.parse(html, formData);
    }

    public parse(htmlString: string, formData?: any): Document {
        this.html = this.parser.parseFromString(htmlString, "text/html");

        // Get all input, textarea and select HTML elements.
        const inputs: NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> =
            this.html.querySelectorAll("input, textarea, select");

        // For each of them, update their value attribute with their actual value.
        // This is made over the new DOM tree, to not remove the placeholders in the original form.
        inputs.forEach((input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): void => {
            input.setAttribute("placeholder", "");
        });
        
        this.html = this.formValuesParser.applyFormValues(this.html, formData);
        
        this.html = this.removeSubmitButton(this.html);

        this.html = this.removeTooltips(this.html);

        this.html = this.handleFormioTabComponentOnePageByEveryTab(this.html) as Document;

        this.html = this.wrapInputElementsOnOverflow(this.html);

        this.html = this.removeCardClass(this.html);

        return this.html;
    }

    private removeCardClass(html: Document): Document {
        const firstChild: HTMLDivElement = html.body.firstChild as HTMLDivElement;

        firstChild.className = firstChild.className.replace("card bg-light", "");

        return html;
    }

    private removeSubmitButton(html: Document): Document {
        const formioComponentSubmit = "formio-component-submit";

        const submitButtons = html.getElementsByClassName(formioComponentSubmit);

        const divElement = document.createElement("div");

        for (let index = 0; index < submitButtons.length; index++) {
            const element = submitButtons[index];

            element.parentNode?.replaceChild(divElement, element);
        }

        return html;
    }

    private removeTooltips(html: Document): Document {
        const tooltips = html.querySelectorAll("[data-tooltip]");

        const divElement = document.createElement("div");

        for (let index = 0; index < tooltips.length; index++) {
            const element = tooltips[index];

            element.parentNode?.replaceChild(divElement, element);
        }

        return html;
    }

    private wrapInputElementsOnOverflow(html: Document): Document {
        const selectors: string = "input, textarea";
        const targetTypes: string[] = ["text", "email", "search", "url", "textarea"];
        const inputs: (HTMLInputElement | HTMLTextAreaElement)[] = Array.from(html.querySelectorAll(selectors));

        for (const input of inputs) {
            if (!targetTypes.includes(input.type)) continue;

            const span: HTMLSpanElement = document.createElement("span");

            span.innerText = input.value || input.placeholder || "";

            span.setAttribute("data-original-name", input.name);
            span.setAttribute("data-original-value", input.value);
            span.setAttribute("readonly", "readonly");

            span.className = input.className;

            span.style.display = "inline-block";
            span.style.boxSizing = "border-box";
            span.style.minHeight = "36px"
            span.style.height = "auto";
            span.style.whiteSpace = "pre-wrap";
            span.style.wordBreak = "break-word";

            input.replaceWith(span);
        }

        return html;
    }

    private handleFormioTabComponentOnePageByEveryTab(html: HTMLElement | Document): HTMLElement | Document {
        const tabsElement: Element[] = Array.from(html.getElementsByClassName(FormioComponentSelectors.Tabs));

        let finalTabs: string = "";
        let tabHeader: HTMLElement = null;
        const tabContents: Array<HTMLElement> = [];

        for (let index = 0; index < tabsElement.length; index++) {
            // Every tab-pane content has first child a <div class="card"
            const divClassCard = <HTMLElement>tabsElement[index].children[0];

            while (divClassCard.childElementCount > 0) {
                let element = <HTMLElement>divClassCard.children[0];

                if (element?.className?.includes("card-header")) {
                    tabHeader = element;
                } else {
                    element = this.handleFormioTabComponentOnePageByEveryTab(element) as HTMLElement
                    tabContents.push(element);
                }

                divClassCard.removeChild(element);
            }

            for (let index = 0; index < tabContents.length; index++) {
                const tabContent = <HTMLElement>tabContents[index];
                tabContent.id = "tab-content-" + index;
                tabContent.style.display = "block";

                // Add TabHeader active at index
                const tabHeaderToAdd = this.modifyTabHeader(tabHeader, index);
                tabHeaderToAdd.id = "tab-header-" + index;

                // Add tabContent.firstChild
                const pageBreak = this.getDivForPageBreak();
                pageBreak.id = "page-break-" + index;

                // Add page-break
                const newElement = document.createElement("div");
                divClassCard.id = "new-div-" + index;

                newElement.appendChild(tabHeaderToAdd);
                newElement.appendChild(tabContent);

                if (index + 1 != tabContents.length) newElement.appendChild(pageBreak);

                finalTabs += newElement.innerHTML;
            }

            divClassCard.innerHTML = finalTabs;
        }

        this.handleTabContentsDisplayNoneStyle(html);

        return html;
    }

    private modifyTabHeader(tabHeader: HTMLElement, activeListItemIndex: number): HTMLElement {
        // TabHeader.firstChild = "ul"
        const tabHeaderList = tabHeader.children[0].children;

        // Add class "active" to li = liIndexActive
        // And the <a> inside the <li>
        // Remove class "active" to others <li>
        for (let index = 0; index < tabHeaderList.length; index++) {
            const li = tabHeaderList[index];
            const liChildAnchorTag = <HTMLElement>li.children[0];

            if (index == activeListItemIndex) {
                if (!li.className.includes("active")) {
                    li.classList.add("active")
                }

                if (!liChildAnchorTag.className.includes("active")) {
                    liChildAnchorTag.classList.add("active");
                }
            } else {
                li.className = li.className.replace("active", "");
                liChildAnchorTag.className = liChildAnchorTag.className.replace("active", "");
            }

        }

        return tabHeader;
    }

    private getDivForPageBreak(): HTMLDivElement {
        const divElement = document.createElement("div");
        divElement.classList.add("page-break");
        divElement.style.pageBreakBefore = "always";

        return divElement;
    }

    private handleTabContentsDisplayNoneStyle(html: HTMLElement | Document): void {
        const selector = "card-body tab-pane";
        const tabPanes = html.getElementsByClassName(selector);

        for (let index = 0; index < tabPanes.length; index++) {
            const element = <HTMLElement>tabPanes[index];

            if (element.style.display !== "block") {
                element.style.display = "block"
            }
        }
    }
}